import { useEffect, useState, lazy, Suspense } from 'react';

const API = import.meta.env.VITE_API_URL;

// Lazy-loaded so @xyflow/react (and its CSS) only download when the Tree tab is opened.
const AffiliateTreeChart = lazy(() => import('./AffiliateTreeChart'));

/**
 * Affiliate Tree tab — structured top-down relationship chart.
 * This wrapper handles the affiliate picker + data fetch; the chart itself
 * (AffiliateTreeChart) is a separate lazy chunk.
 */
export default function AffiliateTree() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [affiliates, setAffiliates] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tree, setTree] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/affiliates/picker`, { credentials: 'include' })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((r) => r.json() as Promise<{ success: boolean; data: any[] }>)
      .then((j) => {
        if (j.success) {
          setAffiliates(j.data || []);
          if (j.data?.[0]) setSelectedId(j.data[0].id);
        }
      })
      .catch(() => { /* picker fetch failed — dropdown stays empty */ });
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setTree(null);
      return;
    }
    setLoading(true);
    fetch(`${API}/admin/affiliates/${selectedId}/tree`, { credentials: 'include' })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((r) => r.json() as Promise<{ success: boolean; data: any }>)
      .then((j) => {
        if (j.success) setTree(j.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedId]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Affiliate Tree</h2>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value="">Select affiliate...</option>
          {affiliates.map((a) => (
            <option key={a.id} value={a.id}>{a.display_name} ({a.referral_code})</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading...</div>
      ) : !tree?.root ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Select an affiliate to view their tree.
        </div>
      ) : (
        <>
          <Suspense
            fallback={
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading chart…</div>
            }
          >
            <AffiliateTreeChart tree={tree} />
          </Suspense>

          <div className="mt-3 text-xs text-gray-500 flex flex-wrap items-center gap-x-4 gap-y-1">
            <strong>Legend:</strong>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> Root affiliate</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Location referral — active plan</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 rounded"></span> Past-due plan</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-slate-400 rounded"></span> No plan</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded"></span> Downline affiliate (also a customer)</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-yellow-500 border-dashed rounded"></span> Affiliate-only downline (no plan)</span>
          </div>
        </>
      )}
    </div>
  );
}
